import {
  Fun,
  Option,
  Widget,
  fromJSX,
  none,
  some
} from 'widgets-for-react';

export type TypeCastingValidator<entity> = entity extends boolean
  ? { kind: 'boolean'; customValidationLogic?: Fun<entity, boolean> }
  : entity extends string
  ? { kind: 'string'; customValidationLogic?: Fun<entity, boolean> }
  : entity extends number
  ? { kind: 'number'; customValidationLogic?: Fun<entity, boolean> }
  : entity extends Array<infer entity>
  ? {
      kind: 'array'
      elementValidator: TypeCastingValidator<entity>
      customValidationLogic?: Fun<entity, boolean>
    }
  : entity extends Object
  ? {
      kind: 'object'
      fieldsValidator: {
        [fieldKey in keyof entity]: TypeCastingValidator<entity[fieldKey]>
      }
      customValidationLogic?: Fun<entity, boolean>
    }
  : { kind: 'unsupported'; customValidationLogic?: Fun<entity, boolean> }

export const canSafelyCast = <entity,>(
  validator: TypeCastingValidator<entity>,
  valueToValidate: any
): Option<entity> => {
  const customValidationLogic = (validator as any).customValidationLogic
  if (validator.kind == 'unsupported') {
    console.error(`Unsupported when parsing ${JSON.stringify(valueToValidate)}`)
    return none()
  } else if (
    validator.kind == 'boolean' ||
    validator.kind == 'number' ||
    validator.kind == 'string'
  ) {
    if (
      validator.kind != typeof valueToValidate ||
      (customValidationLogic != undefined &&
        !customValidationLogic(valueToValidate))
    ) {
      console.error(
        `Error when parsing ${validator.kind} ${JSON.stringify(
          valueToValidate
        )}`
      )
      return none()
    }
  } else if (validator.kind == 'array') {
    const elementValidator = (validator as any).elementValidator
    if (
      !Array.isArray(valueToValidate) ||
      (customValidationLogic != undefined &&
        !customValidationLogic(valueToValidate))
    ) {
      console.error(
        `Error when parsing array ${JSON.stringify(valueToValidate)}`
      )
      return none()
    } else {
      if (
        valueToValidate.find(
          (v) => canSafelyCast<unknown>(elementValidator, v).kind == 'l'
        )
      ) {
        return none()
      }
    }
  } else if (validator.kind == 'object') {
    const fieldsValidator = (validator as any).fieldsValidator
    const allKeys = []
    for (const fieldValidatorKey in fieldsValidator)
      allKeys.push(fieldValidatorKey)
    if (
      'object' != typeof valueToValidate ||
      (customValidationLogic != undefined &&
        !customValidationLogic(valueToValidate))
    ) {
      console.error(
        `Error when parsing object ${JSON.stringify(valueToValidate)}`
      )
      return none()
    } else {
      for (const fieldValidatorKey in fieldsValidator) {
        if (valueToValidate[fieldValidatorKey] == undefined) {
          console.error(
            `Error - field ${fieldValidatorKey} missing - when parsing object ${JSON.stringify(
              valueToValidate
            )}`
          )
          return none()
        }
        if (
          canSafelyCast(
            fieldsValidator[fieldValidatorKey],
            valueToValidate[fieldValidatorKey]
          ).kind == 'l'
        ) {
          return none()
        }
      }
    }
  } else {
    console.error(`Unsupported when parsing ${JSON.stringify(valueToValidate)}`)
    return none()
  }
  return some(valueToValidate as entity)
}

export const wrapHtml2 =
  <A,>(widgets: [Widget<A>, Widget<A>]) =>
  (wrap: Fun<[JSX.Element, JSX.Element], JSX.Element>): Widget<A> =>
    fromJSX((setState) =>
      wrap([widgets[0].run(setState), widgets[1].run(setState)])
    )

export const wrapHtml3 =
  <A,>(widgets: [Widget<A>, Widget<A>, Widget<A>]) =>
  (
    wrap: Fun<[JSX.Element, JSX.Element, JSX.Element], JSX.Element>
  ): Widget<A> =>
    fromJSX((setState) =>
      wrap([
        widgets[0].run(setState),
        widgets[1].run(setState),
        widgets[2].run(setState),
      ])
    )

export const wrapHtml4 =
  <A,>(widgets: [Widget<A>, Widget<A>, Widget<A>, Widget<A>]) =>
  (
    wrap: Fun<[JSX.Element, JSX.Element, JSX.Element, JSX.Element], JSX.Element>
  ): Widget<A> =>
    fromJSX((setState) =>
      wrap([
        widgets[0].run(setState),
        widgets[1].run(setState),
        widgets[2].run(setState),
        widgets[3].run(setState),
      ])
    )

export const inject = <a,>(
  f: (setOuterState: (_: a) => void) => Widget<a>
): Widget<a> => fromJSX((setOuterState) => f(setOuterState).run(setOuterState))
